import { t } from '@lingui/core/macro';
import { extractAmountFromMonetaryValue, isPresent, maxBy, minBy } from '@luminovo/commons';
import {
    colorSystem,
    createNumericValidator,
    fieldConverterNumber,
    Flexbox,
    InputAdornment,
    SearchInput,
} from '@luminovo/design-system';
import { formatDecimal, formatMonetaryValue } from '@luminovo/frontend-utils';
import type { JSX } from 'react';

import { Column, RowData } from '../tanstackTypes';
import { FilterValue, getFilterConfig, NumberRange, SupportedFilterFn } from '../type';
import { createFilterValueUpdater, isFilterValue } from './createFilterValueUpdater';

function parseNumberValue(value: string | undefined): number | null {
    if (!isPresent(value) || value === '' || (typeof value === 'string' && value.trim() === '')) {
        return null;
    }

    const parsedValue = Number(value);
    return Number.isFinite(parsedValue) ? parsedValue : null;
}

function generatePlaceholder<TData extends RowData>(column: Column<TData, unknown>, minOrMax: 'min' | 'max'): string {
    const numberFilterConfig = getFilterConfig(column, 'number');
    if (isPresent(numberFilterConfig)) {
        const facetedMinMaxValues = column.getFacetedMinMaxValues();
        if (isPresent(facetedMinMaxValues)) {
            switch (minOrMax) {
                case 'min':
                    return formatDecimal(facetedMinMaxValues[0], { ifAbsent: '-' });
                case 'max':
                    return formatDecimal(facetedMinMaxValues[1], { ifAbsent: '-' });
            }
        }
    }

    const monetaryValueFilterConfig = getFilterConfig(column, 'monetaryValue');
    if (isPresent(monetaryValueFilterConfig)) {
        const facedValues = Array.from(column.getFacetedUniqueValues().keys());
        const formatAs = column.columnDef.meta?.renderOptionConfig?.default ?? 'default';
        switch (minOrMax) {
            case 'min':
                return formatMonetaryValue(
                    minBy(facedValues, (x) => extractAmountFromMonetaryValue(x)),
                    formatAs,
                    { ifAbsent: '-' },
                );
            case 'max':
                return formatMonetaryValue(
                    maxBy(facedValues, (x) => extractAmountFromMonetaryValue(x)),
                    formatAs,
                    { ifAbsent: '-' },
                );
        }
    }

    const quantityFilterConfig = getFilterConfig(column, 'quantity');
    if (isPresent(quantityFilterConfig)) {
        return t`Normalized`;
    }

    return '-';
}

export function FilterInRange<TData extends RowData>({
    column,
    onClose,
}: {
    column: Column<TData, unknown>;
    onClose: () => void;
}): JSX.Element | null {
    const filterConfig =
        getFilterConfig(column, 'number') ||
        getFilterConfig(column, 'monetaryValue') ||
        getFilterConfig(column, 'quantity');

    if (!isPresent(filterConfig)) {
        return null;
    }

    const handleMinChange = (value: string | undefined) => {
        const numberValue = parseNumberValue(value);
        column.setFilterValue(
            createFilterValueUpdater((old: FilterValue<NumberRange> | undefined) => {
                const oldRange = old?.value as NumberRange | undefined;
                const max = oldRange?.[1] ?? null;
                if (!isPresent(numberValue) && !isPresent(max)) {
                    return undefined;
                }
                return [numberValue, max];
            }, filterConfig.defaultFilterFn),
        );
    };

    const handleMaxChange = (value: string | undefined) => {
        const numberValue = parseNumberValue(value);
        column.setFilterValue(
            createFilterValueUpdater((old: FilterValue<NumberRange> | undefined) => {
                const oldRange = old?.value as NumberRange | undefined;
                const min = oldRange?.[0] ?? null;
                if (!isPresent(numberValue) && !isPresent(min)) {
                    return undefined;
                }
                return [min, numberValue];
            }, filterConfig.defaultFilterFn),
        );
    };

    const filterValue = column.getFilterValue();
    const numberRangeValue =
        isFilterValue<NumberRange>(filterValue) &&
        (filterValue.filterFn === SupportedFilterFn.inNumberRange ||
            filterValue.filterFn === SupportedFilterFn.inMonetaryValueRange ||
            filterValue.filterFn === SupportedFilterFn.inQuantityRange)
            ? (filterValue.value as NumberRange)
            : undefined;
    const valueMin = typeof numberRangeValue?.[0] === 'number' ? String(numberRangeValue[0]) : undefined;
    const valueMax = typeof numberRangeValue?.[1] === 'number' ? String(numberRangeValue[1]) : undefined;

    return (
        <Flexbox gap={4}>
            <SearchInput
                placeholder={generatePlaceholder(column, 'min')}
                value={valueMin}
                onChange={handleMinChange}
                debounceWait={50}
                onKeyEnter={onClose}
                onClear={() => handleMinChange(undefined)}
                style={{ backgroundColor: colorSystem.neutral.white, width: '148px' }}
                formatter={{
                    converter: fieldConverterNumber,
                    validation: createNumericValidator({ required: false }),
                }}
                slotProps={{
                    input: {
                        startAdornment: <InputAdornment position="start">{t`Min`}</InputAdornment>,
                    },
                }}
            />
            <SearchInput
                placeholder={generatePlaceholder(column, 'max')}
                value={valueMax}
                onChange={handleMaxChange}
                debounceWait={50}
                onKeyEnter={onClose}
                onClear={() => handleMaxChange(undefined)}
                style={{ backgroundColor: colorSystem.neutral.white, width: '148px' }}
                formatter={{
                    converter: fieldConverterNumber,
                    validation: createNumericValidator({ required: false }),
                }}
                slotProps={{
                    input: {
                        startAdornment: <InputAdornment position="start">{t`Max`}</InputAdornment>,
                    },
                }}
            />
        </Flexbox>
    );
}

export const FilterInNumberRange = FilterInRange;
export const FilterInMonetaryValueRange = FilterInRange;
export const FilterInQuantityRange = FilterInRange;
